import React, { useState, useRef, useEffect } from "react";
import {
  TextField,
  Button,
  Box,
  Grid,
  FormControlLabel,
  Checkbox,
  Chip,
  Radio,
  RadioGroup,
} from "@mui/material";
import JoyButton from "@mui/joy/Button";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import Popover from "@mui/material/Popover";
import { propertyTypes } from "../../util/flStates";
import RefreshIcon from "@mui/icons-material/Refresh";
import { FaTimes, FaSearch } from "react-icons/fa"; // Import icons
import styles from "./SelectHousePropertyFilter.module.css";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";

const SelectHousePropertyFilter = ({ onApplyFilters, fallbackInfo }) => {
  const [filters, setFilters] = useState({
    priceMin: "",
    priceMax: "",
    beds: "",
    baths: "",
    type: [], // Initialize as an array
    zip: "",
    city: "",
    state: "",
    addressInput: "",
    keyword: "",
    professional: "",
    typeId : null
  });

  const getPlaceholder = (type,value) => {
    return value;
  };


  const addressRef = useRef(null); // Ref for Google Places Autocomplete
  const autocompleteRef = useRef(null); // To store autocomplete instance

  const [popoverState, setPopoverState] = useState({
    price: null,
    beds: null,
    baths: null,
    type: null,
    typeId : null
  });

  // Initialize Google Places Autocomplete
  useEffect(() => {
    if (window.google && window.google.maps && window.google.maps.places) {
      autocompleteRef.current = new window.google.maps.places.Autocomplete(
        addressRef.current,
        {
          types: ["(regions)"], // Restrict results to cities, states, and regions
          componentRestrictions: { country: "us" }, // Restrict to United States
          fields: ["address_components", "formatted_address"],
        }
      );

      // Listener for when a place is selected
      autocompleteRef.current.addListener("place_changed", handlePlaceChanged);
    }
  }, []);

  const handlePlaceChanged = () => {
    const place = autocompleteRef.current.getPlace();

    if (place && place.address_components) {
      let updatedFilters = { ...filters };
      let zip = "";
      let city = "";
      let state = "";

      place.address_components.forEach((component) => {
        const componentType = component.types[0];
        switch (componentType) {
          case "postal_code":
            zip = component.long_name;
            break;
          case "locality":
            city = component.long_name;
            break;
          case "administrative_area_level_1":
            state = component.short_name;
            break;
          default:
            break;
        }
      });

      updatedFilters = {
        ...updatedFilters,
        addressInput: place.formatted_address,
        zip,
        city,
        state,
      };

      setFilters(updatedFilters);
      onApplyFilters(updatedFilters); // Apply the updated filters
    }
  };

  const handleFilterRemove = (filter) => {
    let newFilters = {
      ...filters,
      [filter]: "",
    };

    setFilters(newFilters);
    onApplyFilters(newFilters);
  };

  const handleTypeFilterRemove = (typeId) => {
    let newFilters = {
      ...filters,
      type: filters.type.filter((id) => id !== typeId),
    };

    setFilters(newFilters);
    onApplyFilters(newFilters);
  };

  const handleClick = (event, filter) => {
    setPopoverState({
      ...popoverState,
      [filter]: event.currentTarget,
    });
  };

  const handleClose = (filter) => {
    setPopoverState({
      ...popoverState,
      [filter]: null,
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value,
    });
  };

  const handleCheckboxChange = (event) => {
    const value = parseInt(event.target.value);
    setFilters((prevFilters) => {
      const newTypes = prevFilters.type.includes(value)
        ? prevFilters.type.filter((type) => type !== value)
        : [...prevFilters.type, value];
      return { ...prevFilters, type: newTypes };
    });
  };

  const handleFilterChange = (filterName) => {
    onApplyFilters(filters);
  };

  const resetFilters = (event) => {
    event.preventDefault();

    const resetState = {
      priceMin: "",
      priceMax: "",
      beds: "",
      baths: "",
      type: [],
      zip: "",
      city: "",
      state: "",
      addressInput: "",
      keyword: "",
      professional: "",
      listedBy: "",
      typeId : 6
    };

    setFilters(resetState);
    onApplyFilters(resetState);
  };

  // Helper function to process address input on Enter key press
  const handleAddressChange = (e) => {
    const input = e.target.value;
    setFilters((prevFilters) => ({
      ...prevFilters,
      addressInput: input, // Store raw input for now
    }));
  };

  const handleListedByChange = (event) => {
    setFilters({ ...filters, listedBy: event.target.value });
  };

  const handleTypeIdChange = (event) => {
    setFilters({ ...filters, typeId: event.target.value });
  };

  const clearInput = () => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      addressInput: "", // Clear the input field
    }));
    onApplyFilters({ ...filters, addressInput: "" }); // Reset search with empty input
  };

  const performSearch = () => {
    const { addressInput } = filters;
    const zipRegex = /^\d{5}(?:[-\s]\d{4})?$/; // US ZIP code pattern
    const stateRegex = /^[a-zA-Z]{2}$/; // State code pattern (2 letters)
    const isNumeric = /^\d+$/; // Check if input is purely numeric

    const hasMultipleComponents = /\s/.test(addressInput.trim());

    let updatedFilters = { ...filters };

    if (hasMultipleComponents) {
      updatedFilters = {
        ...updatedFilters,
        zip: "",
        city: "",
        state: "",
        addressInput: addressInput.trim(),
      };
    } else if (zipRegex.test(addressInput)) {
      updatedFilters = {
        ...updatedFilters,
        zip: addressInput,
        city: "",
        state: "",
        addressInput: addressInput,
      };
    } else if (stateRegex.test(addressInput)) {
      updatedFilters = {
        ...updatedFilters,
        state: addressInput.toUpperCase(),
        zip: "",
        city: "",
        addressInput: addressInput,
      };
    } else if (!isNumeric.test(addressInput)) {
      updatedFilters = {
        ...updatedFilters,
        city: addressInput,
        zip: "",
        state: "",
        addressInput: addressInput,
      };
    } else {
      updatedFilters = {
        ...updatedFilters,
        zip: "",
        city: "",
        state: "",
        addressInput: "",
      };
    }

    setFilters(updatedFilters);
    onApplyFilters(updatedFilters);
  };

  // Handle Enter key press
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      performSearch();
    }
  };

  return (
    <div className="filter-section">
      <Box>
        <form noValidate autoComplete="off">
          <Grid container spacing={2} alignItems="center">
            <Grid item key="address-search">
              <div className={styles.addressSearchBox}>
                <input
                  ref={addressRef}
                  type="text"
                  value={filters.addressInput}
                  onChange={handleAddressChange}
                  onKeyDown={handleKeyDown}
                  placeholder={getPlaceholder(fallbackInfo?.type,fallbackInfo?.value)}
                  className={styles.addressInput}
                />
                {filters.addressInput && (
                  <div
                    type="button"
                    className={styles.addressRemoveButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      clearInput();
                    }}
                  >
                    <FaTimes />
                  </div>
                )}
                <div
                  type="button"
                  className={styles.addressSearchButton}
                  onClick={performSearch}
                >
                  <FaSearch />
                </div>
              </div>
            </Grid>

            <Grid item key="listedBy">
              <JoyButton
                color="neutral"
                variant={popoverState.listedBy ? "solid" : "soft"}
                onClick={(event) => handleClick(event, "listedBy")}
                endDecorator={
                  popoverState.listedBy ? (
                    <KeyboardArrowUpIcon />
                  ) : (
                    <KeyboardArrowDownIcon />
                  )
                }
              >
                Listed By
              </JoyButton>
              <Popover
                open={Boolean(popoverState.listedBy)}
                anchorEl={popoverState.listedBy}
                onClose={() => handleClose("listedBy")}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
              >
                <div className={styles.popBody}>
                  <RadioGroup
                    name="listedBy"
                    value={filters.listedBy || ""}
                    onChange={handleListedByChange}
                  >
                    <FormControlLabel
                      value="professional"
                      control={<Radio />}
                      label="Listed by Professional"
                    />
                    <FormControlLabel
                      value="notProfessional"
                      control={<Radio />}
                      label="Not Listed by Professional"
                    />
                  </RadioGroup>
                </div>
                <div className={styles.popFooter}>
                  <Button
                    variant="contained"
                    onClick={() => {
                      handleClose("listedBy");
                      handleFilterChange("listedBy");
                    }}
                    fullWidth
                  >
                    Apply
                  </Button>
                </div>
              </Popover>
            </Grid>

            <Grid item key="typeId">
              <JoyButton
                color="neutral"
                variant={popoverState.typeId ? "solid" : "soft"}
                onClick={(event) => handleClick(event, "typeId")}
                endDecorator={
                  popoverState.listedBy ? (
                    <KeyboardArrowUpIcon />
                  ) : (
                    <KeyboardArrowDownIcon />
                  )
                }
              >
              Listed Type
              </JoyButton>
              <Popover
                open={Boolean(popoverState.typeId)}
                anchorEl={popoverState.typeId}
                onClose={() => handleClose("typeId")}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
              >
                <div className={styles.popBody}>
                  <RadioGroup
                    name="typeId"
                    value={filters.typeId || ""}
                    onChange={handleTypeIdChange}
                  >
                    <FormControlLabel
                      value="1"
                      control={<Radio />}
                      label="Rent"
                    />
                    <FormControlLabel
                      value="2"
                      control={<Radio />}
                      label="Sell"
                    />
                    <FormControlLabel
                      value="6"
                      control={<Radio />}
                      label="All"
                    />
                  </RadioGroup>
                </div>
                <div className={styles.popFooter}>
                  <Button
                    variant="contained"
                    onClick={() => {
                      handleClose("typeId");
                      handleFilterChange("typeId");
                    }}
                    fullWidth
                  >
                    Apply
                  </Button>
                </div>
              </Popover>
            </Grid>

            {["price", "beds", "baths", "type"].map((filter) => (
              <Grid item key={filter}>
                <JoyButton
                  color="neutral"
                  variant={popoverState[filter] ? "solid" : "soft"}
                  onClick={(event) => handleClick(event, filter)}
                  endDecorator={
                    popoverState[filter] ? (
                      <KeyboardArrowUpIcon />
                    ) : (
                      <KeyboardArrowDownIcon />
                    )
                  }
                >
                  {filter === "type"
                    ? "Home Type"
                    : filter.charAt(0).toUpperCase() + filter.slice(1)}
                </JoyButton>
                <Popover
                  open={Boolean(popoverState[filter])}
                  anchorEl={popoverState[filter]}
                  onClose={() => handleClose(filter)}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                >
                  <>
                    {filter === "price" ? (
                      <>
                        <div className="pop-header">
                          <div className="title">Price Range</div>
                        </div>
                        <div className="pop-body">
                          <TextField
                            label="Minimum"
                            name="priceMin"
                            type="number"
                            value={filters.priceMin}
                            onChange={handleChange}
                            placeholder="No Min"
                            inputProps={{ min: 1 }}
                          />
                          <RemoveIcon sx={{ mx: 1 }} />
                          <TextField
                            label="Maximum"
                            name="priceMax"
                            type="number"
                            value={filters.priceMax}
                            onChange={handleChange}
                            placeholder="No Max"
                          />
                        </div>
                        <div className="pop-footer">
                          <Button
                            variant="contained"
                            onClick={() => {
                              handleClose(filter);
                              handleFilterChange("price");
                            }}
                            fullWidth
                          >
                            Apply
                          </Button>
                        </div>
                      </>
                    ) : filter === "beds" ? (
                      <>
                        <div className="pop-header">
                          <div className="title">Number Of Bedrooms</div>
                        </div>
                        <div className="pop-body">
                          <TextField
                            label="Bedrooms"
                            name="beds"
                            type="number"
                            value={filters.beds}
                            onChange={handleChange}
                            placeholder="Any"
                            inputProps={{ min: 0 }}
                          />
                          <AddIcon sx={{ mx: 1 }} />
                        </div>
                        <div className="pop-footer">
                          <Button
                            variant="contained"
                            onClick={() => {
                              handleClose(filter);
                              handleFilterChange("beds");
                            }}
                            fullWidth
                          >
                            Apply
                          </Button>
                        </div>
                      </>
                    ) : filter === "baths" ? (
                      <>
                        <div className="pop-header">
                          <div className="title">Number Of Bathrooms</div>
                        </div>
                        <div className="pop-body">
                          <TextField
                            label="Bathrooms"
                            name="baths"
                            type="number"
                            value={filters.baths}
                            onChange={handleChange}
                            placeholder="Any"
                            inputProps={{ min: 1 }}
                          />
                          <AddIcon sx={{ mx: 1 }} />
                        </div>
                        <div className="pop-footer">
                          <Button
                            variant="contained"
                            onClick={() => {
                              handleClose(filter);
                              handleFilterChange("baths");
                            }}
                            fullWidth
                          >
                            Apply
                          </Button>
                        </div>
                      </>
                    ) : filter === "type" ? (
                      <>
                        <div className="pop-header">
                          <div className="title">Home Type</div>
                        </div>
                        <div className="pop-body multi-check">
                          {Object.values(propertyTypes).map((propertyType) => (
                            <FormControlLabel
                              key={propertyType.id}
                              control={
                                <Checkbox
                                  checked={filters.type.includes(
                                    propertyType.id
                                  )}
                                  onChange={handleCheckboxChange}
                                  value={propertyType.id}
                                />
                              }
                              label={propertyType.label}
                            />
                          ))}
                        </div>
                        <div className="pop-footer">
                          <Button
                            variant="contained"
                            onClick={() => {
                              handleClose(filter);
                              handleFilterChange("type");
                            }}
                            fullWidth
                          >
                            Apply
                          </Button>
                        </div>
                      </>
                    ) : (
                      <></>
                    )}
                  </>
                </Popover>
              </Grid>
            ))}
         
            <Grid item key="filters-selected" className="selected-filters">
              {Object.entries(filters).map(([key, value]) => {
                switch (key) {
                  case "priceMin":
                    if (value) {
                      return (
                        <Chip
                          key={key}
                          label={"> " + value + "$"}
                          variant="outlined"
                          color="error"
                          onClick={() => handleFilterRemove(key)}
                          onDelete={() => handleFilterRemove(key)}
                        />
                      );
                    }
                  case "priceMax":
                    if (value) {
                      return (
                        <Chip
                          key={key}
                          label={"< " + value + "$"}
                          variant="outlined"
                          color="error"
                          onClick={() => handleFilterRemove(key)}
                          onDelete={() => handleFilterRemove(key)}
                        />
                      );
                    }
                  case "beds":
                    if (value) {
                      return (
                        <Chip
                          key={key}
                          label={value + "+ beds"}
                          variant="outlined"
                          color="error"
                          onClick={() => handleFilterRemove(key)}
                          onDelete={() => handleFilterRemove(key)}
                        />
                      );
                    }
                  case "baths":
                    if (value) {
                      return (
                        <Chip
                          key={key}
                          label={value + "+ baths"}
                          variant="outlined"
                          color="error"
                          onClick={() => handleFilterRemove(key)}
                          onDelete={() => handleFilterRemove(key)}
                        />
                      );
                    }
                  case "address":
                  case "keyword":
                    if (value) {
                      return <div key={key}>{value}</div>;
                    }
                  case "type":
                    if (value.length) {
                      return (
                        <>           
                          {value.length ? (
                            value.map((typeId) => (
                              <Chip
                                key={key + "-" + typeId}
                                label={
                                  Object.values(propertyTypes).find(
                                    (type) => type.id === typeId
                                  ).label
                                }
                                variant="outlined"
                                color="error"
                                onClick={() => handleTypeFilterRemove(typeId)}
                                onDelete={() => handleTypeFilterRemove(typeId)}
                              />
                            ))
                          ) : (
                            <></>
                          )}
                        </>
                      );
                    }
                  case "listedBy":
                    if (
                      value === "professional" ||
                      value === "notProfessional"
                    ) {
                      return (
                        <Chip
                          key={key}
                          label={
                            value === "professional"
                              ? "Listed by Professional"
                              : "Not Listed by Professional"
                          }
                          variant="outlined"
                          color="error"
                          onClick={() => handleFilterRemove(key)}
                          onDelete={() => handleFilterRemove(key)}
                        />
                      );
                    }
                    break;
                  default:
                    return null;
                }
              })}
            </Grid>

            <Grid item key="reset">
              <JoyButton
                color="neutral"
                variant={"soft"}
                onClick={(event) => resetFilters(event)}
                endDecorator={<RefreshIcon />}
              >
                Reset
              </JoyButton>
            </Grid>
          </Grid>
        </form>
      </Box>
    </div>
  );
};

export default SelectHousePropertyFilter;
